import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BaseApiService } from '../../base-service/base-api.service';
import { Dictionary } from '../../models/dictionary.model';
import { AppSettings } from '../../settings/app-settings.model';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { SecurityCacheService } from '../../../auth/services/security-cache.service';

@Injectable()
export class ChannelService extends BaseApiService {
    private hubs: Dictionary = new Dictionary([{ key: 'realtimehub', value: { hub: null, connectedSubject$: new Subject<any>() } }]);
    private signalRToken: any;
    private userName: string;
    private msalService: MsalService;

    constructor(public settings: AppSettings, msalService: MsalService) {
        super();
        if (this.settings.api.signalrIntegration !== 'true') {
            return;
        }

        this.userName = localStorage.getItem('signalR:username');
        this.userName = this.userName ? this.userName.toLowerCase() : this.userName;
        this.signalRToken = localStorage.getItem('signalR:token');
        this.msalService = msalService;

        if (!!this.userName && !!this.signalRToken) {
            this.start(this.signalRToken);
        }
    }

    public startSignalR() {
        if (!this.userName || !this.signalRToken) {
            this.get('Foundation/RealtimeMessagingDemo/GetSignalRToken')
                .subscribe(
                    token => {
                        this.signalRToken = token;
                        this.userName = this.authService.userName ? this.authService.userName.toLowerCase() : this.authService.userName;
                        localStorage.setItem('signalR:token', this.signalRToken);
                        localStorage.setItem('signalR:username', this.userName);
                        this.start(token);
                    },
                    err => {
                        console.error('SignalR failed in constructor() of ChannelService.', err);
                    }
                );
        }
    }

    public stopSignalR() {
        console.log('Stopping SignalR');
        this.hubs['realtimehub'].hub.stop();
    }

    /**
     * Sets up the connection and some observable subjects for connection life cycle events
     */
    private setupConnection(token: any) {
        var headers = {};

        // Adds the headers for business unit id and security stamp.
        const businessUnit = SecurityCacheService.businessUnitId;
        if (!headers[this.authService.businessUnitHeaderName] && businessUnit) {
            headers[this.authService.businessUnitHeaderName] = `${businessUnit}`;
        }
        const securityStamp = SecurityCacheService.securityStamp;
        if (!headers[this.authService.securityStampHeaderName] && securityStamp) {
            headers[this.authService.securityStampHeaderName] = `${securityStamp}`;
        }

        const signalROptions: signalR.IHttpConnectionOptions = {
            headers: headers,
            accessTokenFactory: async () => {
                // Initialize msal instance before use.
                await this.msalService.instance.initialize();
                if (!this.msalService.instance.getActiveAccount()) {
                    this.msalService.instance.setActiveAccount(this.msalService.instance.getAllAccounts()[0]);
                }

                const result: AuthenticationResult = await this.msalService.instance.acquireTokenSilent({
                    scopes: [this.settings.security.scope]
                });

                return result.accessToken || '';
            }
        };

        this.hubs['realtimehub'].hub = new signalR.HubConnectionBuilder()
            .withUrl(this.settings.api.realtimeHubUrl + '?username=' + this.userName, signalROptions)
            .build();

        this.hubs['realtimehub'].hub.onclose(() => {
            console.log('Restarting SignalR connection');
            this.startConnection();
        });
    }

    private start(token: any) {
        if (this.settings.api.signalrIntegration !== 'true') {
            return;
        }

        this.setupConnection(token);
        this.startConnection();
    }

    private startConnection() {
        this.hubs['realtimehub'].hub.start()
            .then(() => this.hubs['realtimehub'].connectedSubject$.next(true))
            .catch(error => {
                setTimeout(() => this.startConnection(), 5000);
                console.error('SignalR failed in start() of ChannelService.', error);
            });
    }

    public bindConnectionMessage(hubName: string, channelName: string, callback: (...s: any[]) => void) {
        this.handleSubscription(hubName, channelName, true, callback);
    }

    public unbindConnectionMessage(hubName: string, channelName: string, callback: (...s: any[]) => void) {
        this.handleSubscription(hubName, channelName, false, callback);
    }

    private handleSubscription(hubName: string, channelName: string, subscribe: boolean, callback: (...s: any[]) => void) {
        if (this.hubs.containsKey(hubName)) {
            const registerCallback = () => this.hubs[hubName].hub.on(channelName, callback);
            const deRegisterCallback = () => this.hubs[hubName].hub.off(channelName, callback);

            if (!subscribe) {
                deRegisterCallback();
                return;
            }

            if (localStorage.getItem('signalR:token') == null) {
                this.hubs[hubName].connectedSubject$
                    .pipe(filter(result => !!result))
                    .subscribe(
                        registerCallback,
                        err => console.error('SignalR failed in bindConnectionMessage.', err)
                    );
            } else {
                registerCallback();
            }
        }
    }
}
